/**
 * credentialHistory.js — client-side storage for the player's most-recently
 * used credentials, keyed by game + credential type. Everything lives in
 * `localStorage` (never sent to the server) so it's private and instant.
 *
 * Record shape:
 *   {
 *     playerId:        string,   // ID, username, email, phone (primary key)
 *     playerPassword?: string,   // only for `username_password` games
 *     playerServer?:   string,   // only for `id_server` games
 *     accessMethod?:   string,   // Activision / Google / Facebook
 *     name?:           string,   // e.g. "verifiedPlayerName"
 *     savedAt:         number,   // epoch ms — for ordering
 *   }
 */

const KEY_PREFIX = "ag7_cred_history_v1";
const MAX_PER_KEY = 5;

const bucketKey = (gameId, credType) =>
  `${KEY_PREFIX}:${gameId || "_"}:${credType || "id"}`;

function safeParse(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/** Deduplicate on the composite identity of the entry. Two entries collide
 * when they represent the same account (same ID + same server for id_server;
 * same ID for everything else). Password is intentionally NOT part of the key
 * so re-saving with an updated password overwrites the old row cleanly. */
function makeIdentity(entry) {
  const id = String(entry.playerId || "").trim().toLowerCase();
  const srv = String(entry.playerServer || "").trim();
  return srv ? `${id}|${srv}` : id;
}

export function readHistory(gameId, credType) {
  if (typeof window === "undefined") return [];
  try {
    const rows = safeParse(localStorage.getItem(bucketKey(gameId, credType)));
    return (rows || [])
      .filter((r) => r && String(r.playerId || "").trim().length > 0)
      .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0))
      .slice(0, MAX_PER_KEY);
  } catch (e) {
    console.warn("[credHistory] read failed:", e);
    return [];
  }
}

export function saveEntry(gameId, credType, entry) {
  if (typeof window === "undefined") return [];
  if (!entry || !String(entry.playerId || "").trim()) return [];
  try {
    const key = bucketKey(gameId, credType);
    const existing = safeParse(localStorage.getItem(key));
    const identity = makeIdentity(entry);
    const withoutDup = existing?.filter((r) => makeIdentity(r) !== identity);
    const next = [
      { ...entry, savedAt: Date.now() },
      ...withoutDup,
    ].slice(0, MAX_PER_KEY);
    localStorage.setItem(key, JSON.stringify(next));
    return next;
  } catch (e) {
    console.warn("[credHistory] save failed:", e);
    return [];
  }
}

export function removeEntry(gameId, credType, entry) {
  if (typeof window === "undefined") return [];
  try {
    const key = bucketKey(gameId, credType);
    const existing = safeParse(localStorage.getItem(key));
    const identity = makeIdentity(entry);
    const next = existing?.filter((r) => makeIdentity(r) !== identity);
    localStorage.setItem(key, JSON.stringify(next));
    return next;
  } catch (e) {
    console.warn("[credHistory] remove failed:", e);
    return [];
  }
}

export function clearHistory(gameId, credType) {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(bucketKey(gameId, credType));
  } catch (e) {
    console.warn("[credHistory] clear failed:", e);
  }
}
